import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import {
  useKeystone,
  useSelectedPolicy,
  usePoliciesList,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { logError } from "utils";

// path looks like "/my/property-services/:offerInternalName/:offerStep"
const OFFER_INTERNAL_NAME_PATH_INDEX = 3;

export const useEligibility = () => {
  const keystone = useKeystone();
  const navigate = useNavigate();
  const { data } = useSelectedPolicy();
  const [eligibilityData, setEligibilityData] =
    useState<KeystoneOfferEligibility>();
  const [loading, setLoading] = useState(!data?.keystonePolicyId);

  const { pathname, state: locationState } = useLocation();
  const offerInternalName = pathname.split("/")[OFFER_INTERNAL_NAME_PATH_INDEX];

  useEffect(() => {
    if (!data?.keystonePolicyId) {
      return;
    }

    const handleRedirects = (data: KeystoneOfferEligibility) => {
      if (!data.isEligible) {
        // EXPIRED?
        navigate("/my/property-services/ineligible", { replace: true });
      } else if (
        data.offerStatusCode === "ENROLLED" ||
        data.offerStatusCode === "ORDERED"
      ) {
        navigate(
          `/my/property-services/${data.offeringInternalName}/registered`,
          {
            replace: true,
          },
        );
      } else {
        return;
      }
    };

    const handleNotOk = async (res) => {
      logError(`Creating Offer: ${res.status} ${res.statusText}`);
      navigate("/my/overview");
    };

    // Fetch the eligibility data if needed
    if (
      offerInternalName === (eligibilityData?.offeringInternalName ?? "N/A") &&
      eligibilityData?.insightPolicyId === data.keystonePolicyId
    ) {
      handleRedirects(eligibilityData);
    } else {
      setLoading(true);
      (async () => {
        const offerRes = await keystone.createOffer(
          data.keystonePolicyId,
          offerInternalName,
        );
        if (!isError(offerRes)) {
          handleRedirects(offerRes);
          setEligibilityData(offerRes);
        } else {
          await handleNotOk(offerRes);
          return;
        }
        setLoading(false);
      })();
    }
  }, [
    keystone,
    data?.keystonePolicyId,
    navigate,
    pathname,
    locationState,
    offerInternalName,
    eligibilityData,
  ]);

  return { eligibilityData, loading };
};

export const useMultiPolicyEligibility = (
  keystonePolicyId = "",
  offerInternalName: string,
  skip: boolean = false,
) => {
  const keystone = useKeystone();
  const { data: policies } = usePoliciesList();
  const [loading, setLoading] = useState(!!keystonePolicyId);
  const [additionalPolicyEligibilityData, setAdditionalPolicyEligibilityData] =
    useState<[]>([]);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    const additionalPolicyData =
      policies?.filter((p) => p.keystonePolicyId !== keystonePolicyId) ?? [];
    if (additionalPolicyData.length === 0) {
      return;
    }
    (async () => {
      const additionalPolicyOfferRes: any = await Promise.all(
        additionalPolicyData.map(async (p) => {
          const offerRes = await keystone.createOffer(
            p.keystonePolicyId,
            offerInternalName,
          );
          if (
            !isError(offerRes) &&
            offerRes.isEligible &&
            offerRes.offerStatusCode !== "ENROLLED" &&
            offerRes.offerStatusCode !== "ORDERED"
          ) {
            return offerRes;
          }
        }),
      );
      if (!isError(additionalPolicyOfferRes)) {
        setAdditionalPolicyEligibilityData(
          additionalPolicyOfferRes.filter(Boolean),
        );
        setLoading(false);
      }
      return;
    })();
    return;
  }, [keystonePolicyId, policies, keystone, offerInternalName, skip]);

  return { additionalPolicyEligibilityData, loading };
};
